import Router from 'next/router'
import { MouseEventHandler } from 'react'

import clientSideApollo from '~apollo/clientSideApollo'
import { ONBOARDING_USER_COOKIE_NAME } from '~entities/onboarding/model/constants'
import { LoginResponseFragment } from '~generated/operations'
import { AUTH_DATA_KEYS, CONFLUENCE_COOKIES, GOOGLE_INTEGRATION_COOKIES } from '~lib/constants'
import { clearCookies, isBrowser, setCookies, setServerSideCookie } from '~lib/helpers'
import ROUTES from '~lib/routes'

export type AUTH_STEP =
  | 'EMAIL'
  | 'PASSWORD'
  | 'SIGNUP_EMAIL'
  | 'SIGNUP_PASSWORD'
  | 'FORGOT_PASSWORD'
  | 'PASSWORD_RECOVERY'
  | 'RESET_PASSWORD'
  | 'VERIFY_EMAIL'
  | 'JOIN_WORKSPACE'
  | 'QUICK_SLIDES'
  | 'UPLOAD_EXPERIENCE'
  | 'CREATE_WORKSPACE'
  | 'PROFILE_INFO'
  | 'SSO'
  | 'SSO_ADMIN'
  | 'SSO_ADMIN_INPUT'

export function setAuthCookies({ accessToken, user }: Record<string, any>) {
  setCookies({
    [AUTH_DATA_KEYS.ACCESS_TOKEN]: accessToken,
    [AUTH_DATA_KEYS.USER_ID]: user.id,
  })
}

export async function ssoAdminLogin({ accessToken, user }: LoginResponseFragment): Promise<void> {
  setCookies({
    [AUTH_DATA_KEYS.ACCESS_TOKEN]: accessToken,
    [AUTH_DATA_KEYS.USER_ID]: user.id,
  })
}

export function chooseOrganization({
  organizationId,
  organizationName,
  organizationSlug,
}: {
  organizationId: string
  organizationName: string
  organizationSlug: string
}) {
  setCookies({
    [AUTH_DATA_KEYS.ORGANIZATION_ID]: organizationId,
    [AUTH_DATA_KEYS.ORGANIZATION_NAME]: organizationName,
    [AUTH_DATA_KEYS.ORGANIZATION_SLUG]: organizationSlug,
  })
}

export function clientSideClearAuthCookies(): void {
  clearCookies([
    ...Object.values(AUTH_DATA_KEYS),
    ...Object.values(CONFLUENCE_COOKIES),
    ...Object.values(GOOGLE_INTEGRATION_COOKIES),
    ONBOARDING_USER_COOKIE_NAME,
  ])
}

/**
 * Reset apollo state and cookies, save the theme value and redirect to auth page
 */
export const logout = async () => {
  if (isBrowser()) {
    await clientSideApollo.apollo.clearStore()
    clientSideClearAuthCookies()
    const theme = localStorage.getItem('theme')
    localStorage.clear()
    if (theme) {
      localStorage.setItem('theme', theme)
    }
    Router.push(ROUTES.AUTH)
  }
}

export const handleClickLogout: MouseEventHandler = (evt) => {
  evt.preventDefault()
  logout()
}

/**
 * Creates list of cookies to delete
 */
export const resetPrivatePageCookie = () => {
  const cookie = [...Object.values(AUTH_DATA_KEYS), ...Object.values(CONFLUENCE_COOKIES)].reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {})

  return setServerSideCookie(cookie, {
    path: '/',
    maxAge: -1,
  })
}
